/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";

import { FeeConfirmationTabNames } from "../props";
import { DragDropUploadPDF } from "../../../../../components/upload";
import api from "../../../../../api";
import { useDataLoader } from "react-remote-data-hooks";
import {
  DraftProposalStatus,
  MediaFile,
  ProposalReason,
} from "../../../../../core";
import { toastr } from "react-redux-toastr";
import SendClientApprovalForm from "../SendClientApprovalForm";

import UploadDocument from "./UploadDocument";
import moment from "moment";
import { LeadActivities } from "../../activities/lead-activities";
import { UserAvatar } from "../../../../../components/render-file/UserAvatar";
import {
  useForm,
} from "../../../../../../node_modules/react-hook-form/dist";
import AddNewUserModal from "../../../components/AddNewUser";
import { ContainerLoader } from "../../../../../components/loader";
import { AddNewLandLord } from "../../../../AddLandLord/AddNewLandLord";
import { FileFormats } from '../../../../../utils/fileFormats';
import { isEqual, sortBy } from "lodash";
import AddUser from "../../../../company/AddUser";
import { DocumentCopyType } from '../../../../agreement/Invoice/components/List';
import { UploadFeeLetterApproveConfirm } from "./upload-feeletter-approve-confirm";
import { UploadFeeLetterRejectConfirm } from "./upload-feeletter-reject-confirm";
import { UploadFeeLetterClientFeeConfirmationFile } from "./upload-feeltetter-client-fee-confirmation-file";
import { UploadFeeLetterLandlordFeeConfirmationFile } from "./upload-feeltetter-landlord-fee-confirmation-file";
import { UploadFeeLetterHeader } from "./upload-feeletter-header";

export interface FormProps {
  reason: string;
  comment?: string;
}

const UploadFeeDocument: React.FunctionComponent<{
  onClose?: () => void;
  reload?: () => void;
  documentId: string;
  proposalDocuments?: any[];
  isSigned?: boolean;
  title?: string;
  leadId?: string;
  defaultTab?: FeeConfirmationTabNames;
  showAcceptButton?: boolean;
  showRejectButton?: boolean;
}> = (props) => {
  const [tabName, setTabName] = React.useState<FeeConfirmationTabNames>(
    props?.defaultTab || FeeConfirmationTabNames.ClientFeeConfirmation
  );
  const setElementActive = (tabValue: FeeConfirmationTabNames) => {
    setTabName(tabValue);
  };
  const [isSaving, setSaving] = React.useState(false);
  const [documentId, setDocumentId] = useState(props.documentId);

  const { data: proposalDocument, reload, loading } = useDataLoader<any>(
    (docId: string) =>
      api.proposalDocument.findGenericProposalDocumentStageById(docId!),
    [documentId]
  );

  const [openNewUserModal, setOpenNewUserModal] = React.useState<boolean>(
    false
  );
  const [isReload, setIsReload] = React.useState<boolean>(false);
  const [companyId, setCompanyId] = React.useState<string>("");

  React.useEffect(() => {
    if (!loading) {
      reload(documentId);
    }
  }, [documentId]);

  const { control, errors, handleSubmit } = useForm<FormProps>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const { data: reasons } = useDataLoader<ProposalReason[]>(() =>
    api.proposal.loadAllProposalReason({ entityType: "Documents" })
  );

  const [statusModalOpen, setStatusModalOpen] = React.useState<{
    isOpen?: boolean;
    status?: DraftProposalStatus;
    documentCopyType?: string;
  }>({});

  const [showEdit, setShowEdit] = useState(false);

  const [openApprovalModal, setOpenApprovalModal] = React.useState<{
    isOpen?: boolean;
    approvalType?: string;
    user?: string[];
  }>({});

  const onFileChosen = async (file?: MediaFile) => {
    const createData = {
      proposedPropertyIds: proposalDocument?.proposedPropertyIds,
      documentSettingsId: proposalDocument?.documentSettingsId,
      stageCode: proposalDocument.stageCode,
      draftDocument: file,
      isLandlordCopy:
        tabName === FeeConfirmationTabNames.LandLordFeeConfirmation,
      isClientCopy: tabName === FeeConfirmationTabNames.ClientFeeConfirmation,
    };
    const doc = await api.proposalDocument.createProposalDocumentStage(
      createData
    );
    reload(doc?._id);
    setShowEdit(false);
    props.reload?.();
  };

  const onSend = async (documentCopyType: string) => {
    setSaving(true);
    const createProposalData = {
      approvalType: "Internal",
      documentSettingsId: proposalDocument?.documentSettingsId,
      documentCopyType,
    };

    await api.proposalDocument.requestApproval(
      createProposalData,
      proposalDocument?._id
    );
    setSaving(false);
    toastr.success(
      "Success",
      `${props.title} successfully sent for internal approval`
    );
    props.reload?.();
    reload(proposalDocument?._id);
  };

  const onApprove = async () => {
    setSaving(true);
    const createProposalData = {
      documentSettingsId: proposalDocument?.documentSettingsId,
      documentCopyType: statusModalOpen.documentCopyType,
    };

    await api.proposalDocument.internalApproval(
      createProposalData,
      proposalDocument?._id
    );
    setSaving(false);
    toastr.success("Success", `${props.title} successfully approved`);
    setStatusModalOpen({});
    props.reload?.();
    reload(proposalDocument?._id);
  };

  const onReject = async (data: any) => {
    setSaving(true);
    const createProposalData = {
      documentSettingsId: proposalDocument?.documentSettingsId,
      documentCopyType: statusModalOpen.documentCopyType,
      reason: data.reason,
      comment: data.comment,
      approvalType: "Internal",
    };

    await api.proposalDocument.rejectApproval(
      createProposalData,
      proposalDocument?._id
    );
    setSaving(false);
    toastr.success("Success", `${props.title} successfully rejected`);
    setStatusModalOpen({});
    props.reload?.();
    reload(proposalDocument?._id);
  };

  const onClientFileChosen = (file?: MediaFile) => {
    onSave(file, false);
  };

  const onLandlordFileChosen = (file?: MediaFile) => {
    onSave(file, true);
  };

  const onSave = async (file?: MediaFile, isLandlordCopy?: boolean) => {
    const createData = {
      proposedPropertyIds: proposalDocument?.proposedPropertyIds,
      documentSettingsId: proposalDocument?.documentSettingsId,
      stageCode: "fee_confirmation",
      draftDocument: file,
      isLandlordCopy,
      isClientCopy: !isLandlordCopy,
    };
    const document = await api.proposalDocument.createProposalDocumentStage(
      createData
    );
    props.reload?.();
    reload(document?._id);
  };

  const distinctVersions = (array: any, documentKey: any, key: any) => {
    const seen = new Set();
    return array.filter(
      (obj: any) =>
        !seen.has(obj[documentKey][key]) && seen.add(obj[documentKey][key])
    );
  };

  const renderFile = () => {
    return (
      <div className="row gutter-10">
        <div className="col-sm-12 col-12 time-sheet-upload">
          <div className="form-group font-weight-semibold mb-0">
            <div className="drag-drop-file  position-relative">
              <DragDropUploadPDF
                name="timeSheet"
                id="timeSheet"
                onFileChosen={onFileChosen}
                title={`Upload ${props.title}`}
                accept={FileFormats}
              />
              {tabName === FeeConfirmationTabNames.ClientFeeConfirmation && (
                <div className="mt-3 px-2">
                  <div className="row">
                    <div className="align-items-center col-lg-auto col-md-2 d-flex font-xxs mb-3 mb-md-0">
                      <p className="m-0 pl-3">Download Template</p>
                    </div>
                    <div className="col-lg col-md-10">
                      <ul className="list-unstyled mb-0">
                        <li className="text-primary font-xxs d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Client Fee Letter Format - Detailed.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Client Fee Letter Format - Detailed.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Client - Fee Letter - Standard.doc"
                            download
                          >
                            <span className=" cursor-pointer">
                              Client - Fee Letter - Standard.doc
                            </span>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {tabName === FeeConfirmationTabNames.LandLordFeeConfirmation && (
                <div className="mt-3 px-2 mb-2">
                  <div className="row">
                    <div className="col-lg-2 col-md-2 font-xxs d-flex align-items-center justify-content-center mb-3 mb-md-0">
                      Download Template
                    </div>
                    <div className="col-lg-10 col-md-10">
                      <ul className="list-unstyled mb-0">
                        <li className="text-primary font-xxs d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Landlord - Builder - Fee Letter Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Landlord - Builder - Fee Letter Format.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Owner - Builder - Fee Letter - Renewal of Lease.doc"
                            download
                          >
                            <span className=" cursor-pointer">
                              Owner - Builder - Fee Letter - Renewal of
                              Lease.doc
                            </span>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const currentDocument =
    tabName === FeeConfirmationTabNames.ClientFeeConfirmation
      ? proposalDocument?.document
      : proposalDocument?.seperateDocumentForLandlord;

  const renderAddNewUserHeader = () => {
    return (
      <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-9">
        <div className="row align-items-center">
          <div className="col-auto" onClick={() => setOpenNewUserModal(false)}>
            <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
          </div>
          <div className="col-auto font-base font-weight-bold">
            Add New User
          </div>
        </div>
      </div>
    );
  };

  const NewUserModal = (propertyId: string, approvalType: string) => {
    return (
      <div
        className="detail-poupup position-fixed top-0 w-50 h-100 overflow-auto in"
        style={{ maxWidth: "600px", zIndex: 1055 }}
      >
        {renderAddNewUserHeader()}
        {approvalType === "Client" ? (
          <>
            <AddUser
              onClose={() => setOpenNewUserModal(false)}
              customerCreated={() => setIsReload(true)}
              companyId={companyId}
            />
          </>
        ) : (
          <AddNewLandLord
            onClose={() => setOpenNewUserModal(false)}
            onCreateUser={() => setIsReload(true)}
            propertyId={propertyId || ""}
            approvalType={approvalType}
            companyId={companyId}
            AddNewUser={AddNewUserModal}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`bg-gray-100 detail-poupup  position-fixed top-0 w-100 h-100  in time-sheet-popup`}
        style={{ zIndex: 1052 }}
      >
        <UploadFeeLetterHeader
          onClose={props.onClose}
          showAcceptButton={props.showAcceptButton}
          showRejectButton={props.showRejectButton}
          title={props.title}
          currentDocument={currentDocument}
          documentId={documentId}
          isSaving={isSaving}
          onSend={onSend}
          proposalDocument={proposalDocument}
          setDocumentId={setDocumentId}
          setOpenApprovalModal={setOpenApprovalModal}
          setShowEdit={setShowEdit}
          setStatusModalOpen={setStatusModalOpen}
          showEdit={showEdit}
          tabName={tabName}

        />
        {loading && !proposalDocument ? (
          <ContainerLoader height={200}></ContainerLoader>
        ) : (
          <>
            {!showEdit ? (
              <div className="container-fluid px-0 h-100">
                <div className="">
                  <ul
                    className="d-flex list-unstyled tab-underline font-sm font-weight-semibold border-bottom text-nowrap overflow-auto"
                    id="activity-tab"
                    role="tablist"
                  >
                    <li className="nav-item pr-4 pl-4" role="presentation">
                      <a
                        className={`cursor-pointer nav-link position-relative px-0 py-2 text-gray-600 ${tabName ===
                          FeeConfirmationTabNames.LandLordFeeConfirmation
                          ? "active"
                          : ""
                          }`}
                        onClick={(e) => {
                          setElementActive(
                            FeeConfirmationTabNames.LandLordFeeConfirmation
                          );
                          setOpenApprovalModal({});
                          reload(
                            distinctVersions(
                              props.proposalDocuments?.filter(
                                (x) =>
                                  isEqual(x.proposedPropertyIds, proposalDocument?.proposedPropertyIds) &&
                                  x.stageCode === proposalDocument?.stageCode &&
                                  !!x.seperateDocumentForLandlord
                              ),
                              "seperateDocumentForLandlord",
                              "versionNumber"
                            )?.[0]?._id || proposalDocument?._id
                          );
                        }}
                      >
                        <span className="py-1 d-inline-flex">
                          LandLord Fee Confirmation
                        </span>
                      </a>
                    </li>
                    <li className="nav-item pr-4 pl-4" role="presentation">
                      <a
                        className={`cursor-pointer nav-link position-relative px-0 py-2 text-gray-600 ${tabName ===
                          FeeConfirmationTabNames.ClientFeeConfirmation
                          ? "active"
                          : ""
                          }`}
                        onClick={(e) => {
                          setOpenApprovalModal({});
                          setElementActive(
                            FeeConfirmationTabNames.ClientFeeConfirmation
                          );
                          reload(
                            distinctVersions(
                              props.proposalDocuments?.filter(
                                (x) =>
                                  isEqual(x.proposedPropertyIds, proposalDocument?.proposedPropertyIds) &&
                                  x.stageCode === proposalDocument?.stageCode &&
                                  !!x.document
                                // && x.isLatest
                              ),
                              "document",
                              "versionNumber"
                            )?.[0]?._id || proposalDocument?._id
                          );
                        }}
                      >
                        <span className="py-1 d-inline-flex">
                          Client Fee Confirmation
                        </span>
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="h-100 mt-2 px-4 row">
                  <div
                    className={`${tabName === FeeConfirmationTabNames.Version
                      ? "col-12"
                      : "col-8"
                      }`}
                  >
                    {tabName ===
                      FeeConfirmationTabNames.ClientFeeConfirmation && (
                        <>
                          {loading ? (
                            <ContainerLoader height={200}></ContainerLoader>
                          ) : (
                            <>
                              {proposalDocument?.document?.draftDocument
                                ?.location ? (
                                <UploadDocument
                                  proposalDocument={proposalDocument}
                                  reload={(id?: string) => {
                                    props.reload?.();
                                    reload(id || proposalDocument?._id);
                                  }}
                                  onEdit={() => setShowEdit(true)}
                                  versions={distinctVersions(
                                    props.proposalDocuments?.filter(
                                      (x) =>
                                        Array.isArray(proposalDocument?.proposedPropertyIds) &&
                                        isEqual(sortBy(proposalDocument?.proposedPropertyIds), sortBy(x.proposedPropertyIds)) &&
                                        x.stageCode === proposalDocument?.stageCode &&
                                        !!x.document
                                    ),
                                    "document",
                                    "versionNumber"
                                  )}
                                  title={props.title}
                                  isLandlordCopy={false}
                                ></UploadDocument>
                              ) : (
                                <div className="row m-0">
                                  <div className="col-12 bg-white pt-3  my-2 border pb-0 pl-0 pr-0">
                                    <div className="d-flex align-items-center mb-3 px-4">
                                      <label className="font-md font-weight-semibold mb-0 ">
                                        Client Fee Confirmation
                                      </label>
                                    </div>
                                    <div className="w-100">
                                      <div>
                                        {<UploadFeeLetterClientFeeConfirmationFile
                                          onClientFileChosen={onClientFileChosen}
                                          title={props.title}

                                        />}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}{" "}
                            </>
                          )}
                        </>
                      )}

                    {tabName ===
                      FeeConfirmationTabNames.LandLordFeeConfirmation && (
                        <>
                          {loading ? (
                            <ContainerLoader height={200}></ContainerLoader>
                          ) : (
                            <>
                              {proposalDocument?.seperateDocumentForLandlord
                                ?.draftDocument?.location ? (
                                <UploadDocument
                                  proposalDocument={proposalDocument}
                                  reload={(id?: string) => {
                                    props.reload?.();
                                    reload(id || proposalDocument?._id);
                                  }}
                                  onEdit={() => setShowEdit(true)}
                                  versions={distinctVersions(
                                    props.proposalDocuments?.filter(
                                      (x) =>
                                        Array.isArray(proposalDocument?.proposedPropertyIds) &&
                                        isEqual(sortBy(proposalDocument?.proposedPropertyIds), sortBy(x.proposedPropertyIds)) &&
                                        x.stageCode === proposalDocument?.stageCode &&
                                        !!x.seperateDocumentForLandlord
                                    ),
                                    "seperateDocumentForLandlord",
                                    "versionNumber"
                                  )}
                                  title={props.title}
                                  isLandlordCopy
                                ></UploadDocument>
                              ) : (
                                <div className="row m-0">
                                  <div className="col-12 bg-white pt-3   my-2 border pb-0 pl-0 pr-0">
                                    <div className="d-flex align-items-center mb-3 px-4">
                                      <label className="font-md font-weight-semibold mb-0 ">
                                        LandLord Fee Confirmation
                                      </label>
                                    </div>
                                    <div className="w-100">
                                      <div>
                                        <UploadFeeLetterLandlordFeeConfirmationFile
                                          onLandlordFileChosen={onLandlordFileChosen}
                                          title={props.title}

                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                  </div>
                  {tabName !== FeeConfirmationTabNames.Version && (
                    <div className="col-md-4 col-12 ">
                      {(proposalDocument?.signedClientDocument
                        ?.clientSignedBy ||
                        proposalDocument?.signedLandlordDocument
                          ?.landLordSignedBy) && (
                          <ul
                            className="list-unstyled bg-white border d-flex"
                            style={{ borderRadius: "4px" }}
                          >
                            {proposalDocument?.signedClientDocument
                              ?.clientSignedBy && (
                                <li className="border-right w-50 py-3 px-3 d-flex flex-wrap align-items-center">
                                  <h6 className="font-sm font-weight-semibold text-gray-500">
                                    Client Acceptance & Agreed By
                                  </h6>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="figure"
                                      style={{ width: "26px", height: "26px" }}
                                    >
                                      <UserAvatar
                                        id={
                                          proposalDocument?.signedClientDocument
                                            ?.clientSignedBy?._id
                                        }
                                        className="img-fluid img-cover"
                                        alt=""
                                        title={
                                          proposalDocument?.signedClientDocument
                                            ?.clientSignedBy?.fullName
                                        }
                                        imageUrl={
                                          proposalDocument?.signedClientDocument
                                            ?.clientSignedBy?.avatar?.location
                                        }
                                        image={
                                          proposalDocument?.signedClientDocument
                                            ?.clientSignedBy?.avatar
                                        }
                                      ></UserAvatar>
                                    </div>
                                    <div className="fig-caption font-sm font-weight-semibold pl-2">
                                      {
                                        proposalDocument?.signedClientDocument
                                          ?.clientSignedBy?.fullName
                                      }{" "}
                                      <span className="d-block text-gray-500">
                                        on{" "}
                                        {moment(
                                          proposalDocument?.signedClientDocument
                                            ?.clientSignedAt
                                        ).format("DD/MMM/YYYY")}{" "}
                                      </span>
                                    </div>
                                  </div>
                                </li>
                              )}

                            {proposalDocument?.signedLandlordDocument
                              ?.landLordSignedBy && (
                                <li className="w-50 py-3 px-3 d-flex flex-wrap align-items-center">
                                  <h6 className="font-sm font-weight-semibold text-gray-500">
                                    Vendor/ Owner Acceptance & Agreed By
                                  </h6>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="figure"
                                      style={{ width: "26px", height: "26px" }}
                                    >
                                      <UserAvatar
                                        id={
                                          proposalDocument?.signedLandlordDocument
                                            ?.landLordSignedBy?._id
                                        }
                                        className="img-fluid img-cover"
                                        alt=""
                                        title={
                                          proposalDocument?.signedLandlordDocument
                                            ?.landLordSignedBy?.fullName
                                        }
                                        imageUrl={
                                          proposalDocument?.signedLandlordDocument
                                            ?.landLordSignedBy?.avatar?.location
                                        }
                                        image={
                                          proposalDocument?.signedLandlordDocument
                                            ?.landLordSignedBy?.avatar
                                        }
                                      ></UserAvatar>
                                    </div>
                                    <div className="fig-caption font-sm font-weight-semibold pl-2">
                                      {
                                        proposalDocument?.signedLandlordDocument
                                          ?.landLordSignedBy?.fullName
                                      }{" "}
                                      <span className="d-block text-gray-500">
                                        on{" "}
                                        {moment(
                                          proposalDocument?.signedLandlordDocument
                                            ?.landLordSignedAt
                                        ).format("DD/MMM/YYYY")}{" "}
                                      </span>
                                    </div>
                                  </div>
                                </li>
                              )}
                          </ul>
                        )}

                      {documentId && (
                        <LeadActivities
                          leadId={props.leadId!}
                          documentId={documentId!}
                          onUpdate={() => {
                            // 
                          }}
                          stageCode={proposalDocument?.stageCode}
                          documentCopyType={
                            tabName === FeeConfirmationTabNames.ClientFeeConfirmation
                              ? DocumentCopyType.ClientCopy
                              : DocumentCopyType.LandlordCopy
                          }
                          genericProposalDocumentId={distinctVersions(
                            props.proposalDocuments?.filter(
                              (x) =>
                                Array.isArray(proposalDocument?.proposedPropertyIds) &&
                                isEqual(sortBy(proposalDocument?.proposedPropertyIds), sortBy(x.proposedPropertyIds)) &&
                                x.stageCode === proposalDocument?.stageCode &&
                                !!x.document
                            ),
                            "document",
                            "versionNumber"
                          ).filter((data: any) => data?.document?.versionNumber === 1)?.[0]?._id}
                        ></LeadActivities>
                      )}
                    </div>
                  )}
                </div>
                {statusModalOpen.isOpen &&
                  statusModalOpen.status === DraftProposalStatus.Approved ? (
                  <UploadFeeLetterApproveConfirm
                    isSaving={isSaving}
                    onApprove={onApprove}
                    setStatusModalOpen={setStatusModalOpen}
                    title={props.title}
                  />
                ) : null}
                {statusModalOpen.isOpen &&
                  statusModalOpen.status === DraftProposalStatus.Rejected ? (
                  <UploadFeeLetterRejectConfirm
                    control={control}
                    errors={errors}
                    handleSubmit={handleSubmit}
                    isSaving={isSaving}
                    onReject={onReject}
                    reasons={reasons}
                    setStatusModalOpen={setStatusModalOpen}
                    title={props.title}
                    
                  />
                ) : (
                  <></>
                )}
              </div>
            ) : (
              <div className="row m-0">
                <div className="col-8 bg-white pt-3  mx-4 my-2 border pb-4 pl-0 pr-0">
                  <div className="d-flex align-items-center mb-3 px-4">
                    <label className="font-md font-weight-semibold mb-0 ">
                      {props.title || "Term Sheet"}
                    </label>
                  </div>
                  <div className="w-100">
                    <div>{renderFile()}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="modal-backdrop fade show" style={{ zIndex: 1051 }}></div>
      {openApprovalModal.isOpen && (
        <SendClientApprovalForm
          onClose={() => setOpenApprovalModal({})}
          approvalType={openApprovalModal.approvalType}
          proposalId={proposalDocument?.proposedPropertyDetails[0]?.proposalId}
          documentSettingsId={proposalDocument?.documentSettingsId}
          onSuccess={() => {
            setOpenApprovalModal({});
            props.reload?.();
            reload(proposalDocument?._id);
          }}
          documentId={proposalDocument?._id}
          title={props.title}
          documentCopyType={
            openApprovalModal.approvalType === "Client"
              ? "Client Copy"
              : "Landlord Copy"
          }
          onAddUser={(compId) => {
            setCompanyId(compId);
            setOpenNewUserModal(true);
            setIsReload(false);
          }}
          proposedPropertyId={
            proposalDocument?.proposedPropertyDetails[0]?.propertyDetails
              ?.propertyId
          }
          companyDetails={proposalDocument?.companyDetails}
          isReloadUser={isReload}
        ></SendClientApprovalForm>
      )}
      {openNewUserModal &&
        NewUserModal(
          proposalDocument?.proposedPropertyDetails[0]?.propertyDetails
            ?.propertyId,
          openApprovalModal?.approvalType || ""
        )}
    </>
  );
};

export default UploadFeeDocument;
